'use client';
import { Button, Tooltip } from '@/shared/components';
import React, { useEffect, useMemo, useState } from 'react';
import { add, multiply, number, subtract } from 'mathjs';
import { useQueryFreightAndVat } from '@/hooks/cart.hook';
import { Trans } from 'react-i18next';
import { useRouter } from 'next/navigation';
import { useAppStore } from '@/store/app-store';
import { NAMESPACE, useLang } from '@/hooks/use-lang';
import { useProductStore } from '../product-store';
import { useCartStore } from '../cart-store';
import { usePriceStore } from '@/store/price-store';
import { FreightOrVatParams } from '@/types/cart';
import { redirectLoginPage } from '@/app/utils';
import { usePriceFormat } from '@/hooks/use-price-format';
import { CURRENCY } from '@/app/utils/constant';
import Link from 'next/link';
import { useCountry } from '@/hooks/use-country';
import { useEventTracking } from '@/shared/tracking/use-tracking';
import { DownloadQuotation } from '@/shared/pageComponents/download-quotation';
import { CartQtpModel } from './cart-qtp-model';

export const CartSummary = () => {
  const [queryFreightVatKey, setQueryFreightVatKey] = useState<FreightOrVatParams>();
  const { isCN, isKR } = useCountry();
  const { shipToList, isAnonymous, userInfo, configByCountry } = useAppStore();
  const { productPrice, promotionInfo, fbpSkuInfo } = usePriceStore();
  const productStore = useProductStore();
  const { t } = useLang(NAMESPACE.cart);
  const { t: commonT } = useLang(NAMESPACE.common);
  const cartStore = useCartStore();
  const { data: freightVatData } = useQueryFreightAndVat(queryFreightVatKey);
  const [freight, setFreight] = useState('');
  const [vat, setVat] = useState('');
  const router = useRouter();
  const getFormattedPrice = usePriceFormat();
  const { trackEvent } = useEventTracking();
  const [qtpConfirmModal, setQtpConfirmModal] = useState(false);
  const checkIfSplitCart = (product) => {
    if (promotionInfo && promotionInfo?.promoType === 'FBP' && product?.canApplyPromotion !== undefined) {
      return true;
    }
    return false;
  };
  const selectedProducts = useMemo(
    () => productStore.products.filter((p) => cartStore.selectedCatalogNumbers.includes(p.catalogNumber)),
    [cartStore.selectedCatalogNumbers, productStore.products]
  );
  const totalQuantity = useMemo(
    () => selectedProducts.reduce((prev, curr) => prev + curr.quantity, 0),
    [selectedProducts]
  );
  const listPriceTotal = useMemo(() => {
    return selectedProducts.reduce((prev, curr) => {
      const listPrice = checkIfSplitCart(curr)
        ? fbpSkuInfo[curr.catalogNumber + '_' + curr.canApplyPromotion]?.listPrice ?? 0
        : productPrice[curr.catalogNumber]?.listPrice ?? 0;
      if (number(listPrice) === -1) return prev;
      return add(number(prev), multiply(number(listPrice), curr.quantity));
    }, 0);
  }, [selectedProducts, productPrice, fbpSkuInfo]);

  const bestPriceTotal = useMemo(() => {
    let bestPrice = 0;
    selectedProducts.forEach((p) => {
      const price = checkIfSplitCart(p)
        ? fbpSkuInfo[p.catalogNumber + '_' + p.canApplyPromotion]
        : productPrice[p.catalogNumber];
      bestPrice = add(bestPrice, multiply(number(price?.bestPrice ?? 0), p.quantity));
    });
    return bestPrice;
  }, [selectedProducts, productPrice, fbpSkuInfo]);

  const totalSavings = useMemo(() => subtract(bestPriceTotal, listPriceTotal), [listPriceTotal, bestPriceTotal]);

  const displayedVatTotal = useMemo(() => {
    if (!isKR || !vat) return 0;

    return selectedProducts.reduce((sum, product) => {
      const price = checkIfSplitCart(product)
        ? fbpSkuInfo[product.catalogNumber + '_' + product.canApplyPromotion]
        : productPrice[product.catalogNumber];
      const productSubTotal = multiply(number(price?.bestPrice ?? 0), product.quantity);
      const productVat = Math.round(number(multiply(number(vat), productSubTotal)));
      console.log('productVat', productVat, 'productSubTotal', productSubTotal, 'vat', vat);
      return add(sum, productVat);
    }, 0);
  }, [isKR, vat, selectedProducts, productPrice, fbpSkuInfo, promotionInfo]);

  const totalPrice = useMemo(() => {
    if (!freight && !vat) return bestPriceTotal;
    const fee = isCN ? freight : displayedVatTotal;
    if (!isCN) {
      const krBestPriceTotal = Math.trunc(bestPriceTotal);
      const krFee = Math.trunc(number(fee));
      return add(krBestPriceTotal, krFee);
    }
    return add(bestPriceTotal, number(fee));
  }, [vat, freight, bestPriceTotal]);

  const onCheckout = () => {
    if (isAnonymous) {
      redirectLoginPage();
      return;
    }
    const addressKey = cartStore.address?.addressKey;
    if (!addressKey) {
      return;
    }
    router.push(`/checkout?address=${addressKey}&cartId=${cartStore.cartId}`);
  };
  useEffect(() => {
    if (freightVatData) {
      if (freightVatData?.code === '200') {
        setFreight(freightVatData?.data?.freight ?? '');
        setVat(freightVatData?.data?.vatRatio ?? '');
        cartStore.setIsFreightVatError(false);
      } else {
        setFreight(null);
        cartStore.setIsFreightVatError(true);
      }
    }
  }, [freightVatData]);

  useEffect(() => {
    const validCN = isCN && !isAnonymous && bestPriceTotal > 0 && cartStore.address;
    const validKR = isKR && bestPriceTotal > 0;
    if (validCN || validKR) {
      setQueryFreightVatKey({
        categoryCode03: cartStore.address?.categoryCode03,
        provinceCode: cartStore.address?.provinceCode,
        totalPrice: bestPriceTotal.toString(),
        itemList: productStore.products
          .filter((e) => cartStore.selectedCatalogNumbers.includes(e.catalogNumber))
          .map((product) => {
            //const price = productPrice[product.catalogNumber];
            const price = checkIfSplitCart(product)
              ? fbpSkuInfo[product.catalogNumber + '_' + product.canApplyPromotion]
              : productPrice[product.catalogNumber];
            return {
              catalogNumber: product.catalogNumber,
              productTitle: product.productTitle,
              unitSize: product.unitSize,
              quantity: product.quantity,
              price: (price?.bestPrice ?? '0').toString(),
            };
          }),
      });
    }
    if (!bestPriceTotal) {
      setFreight(null);
      setQueryFreightVatKey(null);
    }
  }, [cartStore.address, bestPriceTotal, productPrice, fbpSkuInfo]);

  const proceedCheckoutDisabled = useMemo(() => {
    if (isKR && userInfo.isLightUser) return true;
    if (isKR && userInfo.isFullUser && !cartStore.address?.isActive) return true;
    if (cartStore.isFreightVatError) return true;
    return !isAnonymous && (totalQuantity === 0 || !shipToList || shipToList.length === 0);
  }, [isAnonymous, totalQuantity, shipToList, cartStore, userInfo]);
  return (
    <div className="cart-summary">
      <div className=" border border-solid border-gray-450 rounded px-6 pt-6 pb-8 flex flex-col text">
        <h1 className="font-medium">{t('cart.summary')}</h1>
        <div className="grid grid-cols-3 mt-[14px] text-gray-1100 text-sm gap-y-1">
          <span className="col-span-2">{t('product.price', { count: totalQuantity })}</span>
          <span className="justify-self-end">{getFormattedPrice(number(listPriceTotal))}</span>

          <span className="col-span-2">{t('total.savings')}</span>
          <span className="justify-self-end">{getFormattedPrice(totalSavings)}</span>

          <span className="font-bold col-span-2">{commonT('subtotal')}</span>

          <span className="justify-self-end">{getFormattedPrice(bestPriceTotal)}</span>

          <span className="col-span-3 font-bold mt-4">{t('shipping.miscellaneous')}</span>

          {isCN && freight && (
            <>
              <span className="mt-3 col-span-2 flex items-center ">
                {t('freight.charge')}
                {number(freight) > 0 && (
                  <Tooltip
                    placement="top"
                    maxWidth={300}
                    title={
                      <Trans
                        t={t}
                        i18nKey="china.freight.tip"
                        components={[
                          <Link className="cursor-pointer" href="/freight" target="_blank" key="freight"></Link>,
                        ]}
                      />
                    }
                  >
                    <i className="icon-24-info-bold ml-1 cursor-pointer text-blue-500 relative" />
                  </Tooltip>
                )}
              </span>

              <span className="justify-self-end mt-3">{getFormattedPrice(number(freight))}</span>
            </>
          )}
          {isKR && vat && (
            <>
              <span className="mt-3 col-span-2">{t('vat')}</span>
              <span className="justify-self-end mt-3">{getFormattedPrice.roundVat(displayedVatTotal)}</span>
            </>
          )}

          <span className="font-bold text-base mt-[31px] self-end ">{t('total.price')}</span>
          {!isAnonymous ? (
            <span className="justify-self-end text-[22px] leading-7 mt-[31px] font-light flex col-span-2 gap-x-1">
              <span>{CURRENCY} </span>
              <span>{getFormattedPrice(totalPrice)}</span>
            </span>
          ) : (
            <Button
              type="link"
              className="p-0 text-left flex items-center col-span-2 justify-self-end mt-[31px] h-6  print:hidden"
              onClick={() => {
                trackEvent({
                  action: 'login',
                });
                redirectLoginPage();
              }}
            >
              {t('sign.in')}
            </Button>
          )}
        </div>
        <Button
          size="medium"
          className="mt-6 print:hidden flex flex-1 items-center justify-center custom-proceed-btn"
          // only anonymous and user with active address(not inactive, include padding address)
          disabled={proceedCheckoutDisabled}
          onClick={() => {
            const qtpSKUMap = usePriceStore.getState()?.promotionInfo?.qtpSKUMap;
            if (qtpSKUMap) {
              const selectedSKUs = new Set(cartStore.selectedCatalogNumbers);

              for (const [sku, info] of Object.entries(qtpSKUMap)) {
                if (info.required && !selectedSKUs.has(sku)) {
                  setQtpConfirmModal(true);
                  return;
                }
              }
            }

            onCheckout();
          }}
        >
          {t('proceed.to.checkout')}
        </Button>

        {configByCountry?.isShowDownloadQuotationInCart && (
          <DownloadQuotation
            disabled={totalQuantity === 0}
            className="print:hidden flex flex-1 items-center justify-center w-full p-0 mt-3"
            subTotal={getFormattedPrice(bestPriceTotal)}
            vat={getFormattedPrice.roundVat(displayedVatTotal)}
            totalPrice={getFormattedPrice(totalPrice)}
            products={selectedProducts}
          >
            {t('download.quotation')}
          </DownloadQuotation>
        )}
      </div>

      {/* hidden below for now */}
      {/* <div className="flex justify-center gap-x-4 items-center mt-[10px] print:hidden">
        <Button type="link" className="p-0 text-left flex items-center">
          <span className="text-sm font-normal">{t('technical.support')}</span>
        </Button>
        <span className="text-blue-500">|</span>
        <Button type="link" className="p-0 text-left flex items-center">
          <span className="text-sm font-normal">{t('Customer Service')}</span>
        </Button>
      </div> */}

      <CartQtpModel
        type="mandatory"
        currentItem={selectedProducts}
        qtpConfirmModal={qtpConfirmModal}
        handleClose={() => setQtpConfirmModal(false)}
        handleSave={() => {}}
      />
    </div>
  );
};
